import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCrashReport } from '../../api/moderator/crashes/hooks';
import { ApiError } from '../../api/_shared/api-error';
import { Button } from '../../components/button';
import { Card } from '../../components/card';
import { FormField, FormMessage, Input } from '../../components/form-field';
import { StatusLine } from '../../components/status-line';
import { CommonHeader } from '../shell/common-header';
import styles from './crash-report-lookup.module.css';

/** Available to moderators and to admins through their moderation dashboard. */
export function CrashReportLookup({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [code, setCode] = useState('');
  const query = useCrashReport(code);
  const report = query.data?.report;
  const status = query.isFetching ? t('crashSupport.loading') : query.isError
    ? t(query.error instanceof ApiError && query.error.status === 404 ? 'crashSupport.missing' : 'crashSupport.failed')
    : report ? t('crashSupport.found') : '';
  return (
    <div className="portal-shell portal-container">
      <CommonHeader title={t('crashSupport.title')} showBackButton onBackClick={onBack} />
      <main className="portal-main">
        <div className="portal-content">
          <Card className={styles.panel}>
            <p>{t('crashSupport.description')}</p>
            <form className={styles.form} onSubmit={(event) => {
              event.preventDefault();
              const value = input.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1').match(/.{1,4}/g)?.join('-') ?? '';
              if (value === code) void query.refetch();
              else setCode(value);
            }}>
              <FormField label={t('crashSupport.code')} required>
                {(field) => <Input {...field} value={input} onChange={(event) => setInput(event.target.value)} maxLength={24} required autoComplete="off" spellCheck={false} />}
              </FormField>
              <Button type="submit" variant="gold" disabled={query.isFetching || !input.trim()}>{t('crashSupport.find')}</Button>
            </form>
            {query.isError ? <FormMessage>{status}</FormMessage> : <p>{status}</p>}
            <StatusLine>{query.isError ? '' : status}</StatusLine>
            {report && (
              <section aria-label={t('crashSupport.details')}>
                <h2>{query.data?.id}</h2>
                <p>{t('crashSupport.version', { version: report.version, build: report.build })}</p>
                <pre className={styles.details}>{JSON.stringify(report, null, 2)}</pre>
              </section>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
